import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AddUserDrawer from "./AddUserDrawer";
import AssignTargetDrawer from "./Assigntargetdrawer";

import {
  Download,
  MoreHorizontal,
  Pencil,
  Trash2,
  Target,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

// Importing the endpoints that use the unified 'api.js' configuration
import { getAllUsers, deleteUser } from "../api/auth";

export default function Users() {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // Structural Core Data States
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState("");
  
  // Interface Layout States
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [selected, setSelected] = useState([]);
  const [toast, setToast] = useState("");
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [activeDropdownId, setActiveDropdownId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [targetUser, setTargetUser] = useState(null); // { usr_id, usr_name } of the row being assigned a target, or null

  const filters = ["All", "sales", "purchase", "finance","preSales","BidManager"];

  // ==========================================
  // FETCH ALL USERS FROM FLASK API
  // ==========================================
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setApiError("");
      
      // Axios interceptors handle adding Bearer Token dynamically
      const response = await getAllUsers();
      if (response.data && response.data.success) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setApiError(
        error.response?.data?.message || "Failed to load system users from the database."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Dropdown dismissal event listener (Closes action window when clicking outside)
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdownId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ==========================================
  // LIVE ENDPOINT DELETION HANDLER
  // ==========================================
  const handleDeleteClick = async (user) => {
    setActiveDropdownId(null);
    if (!window.confirm(`Are you sure you want to permanently delete ${user.usr_name}?`)) {
      return;
    }

    try {
      // Deleting directly via interceptor route matching auth.py endpoint
      const response = await deleteUser(user.usr_id);
      if (response.data && response.data.success) {
        setToast("User account deleted successfully! ✅");
        setSelected((prev) => prev.filter((id) => id !== user.usr_id));
        
        // Reload fresh context rows from DB
        fetchUsers();
        setTimeout(() => setToast(""), 3000);
      }
    } catch (error) {
      alert(error.response?.data?.message || "Unauthorized action context or network error.");
    }
  };

  // ==========================================
  // FILTERS & CLIENT SEARCH LOGIC
  // ==========================================
  const filtered = users.filter((user) => {
    const matchesSearch =
      user.usr_name?.toLowerCase().includes(search.toLowerCase()) ||
      user.usr_employee_id?.toLowerCase().includes(search.toLowerCase());

    const matchesFilter =
      activeFilter === "All" ? true : user.usr_department === activeFilter;

    return matchesSearch && matchesFilter;
  });

  // ==========================================
  // PAGINATION (based on the filtered results)
  // ==========================================
  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paginatedUsers = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
    setSelected([]);
  };

  // Back to page 1 whenever the search text or department filter changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [search, activeFilter]);

  // Never sit on a page that no longer exists (e.g. after a delete).
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(filtered.map((user) => user.usr_id));
    } else {
      setSelected([]);
    }
  };

  const toggleRow = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <div className="ml-[60px] pt-16 p-3 sm:p-6 bg-[#F1F5F9] min-h-screen mt-12 md:h-[calc(100vh-3rem)] md:min-h-0 md:flex md:flex-col md:overflow-hidden">
      {/* TOAST NOTIFICATION */}
      {toast && (
        <div className="fixed top-20 right-6 bg-black text-white px-4 py-2 rounded shadow z-50 text-sm animate-in fade-in slide-in-from-top-2 duration-200">
          {toast}
        </div>
      )}

      {/* HEADER */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800">Users</h1>
          <p className="text-sm text-slate-500 mt-1">
            Users &gt; List | Total Records: {filtered.length}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsAddUserOpen(true)}
            className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition shadow-md shadow-blue-200"
          >
            + Add User
          </button>
        </div>
      </div>

      {/* ERROR ANCHOR ALERTS */}
      {apiError && (
        <div className="mb-4 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl">
          <AlertCircle size={20} className="text-red-500 shrink-0" />
          <div className="text-sm font-semibold">{apiError}</div>
        </div>
      )}

      {/* FILTER + SEARCH BOX */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="text"
            className="w-full sm:flex-1 sm:min-w-[250px] px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Search by Employee ID or User Name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {filters.map((item) => (
            <button
              key={item}
              onClick={() => { setActiveFilter(item); setSelected([]); }}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                activeFilter === item
                  ? "bg-blue-600 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {item === "All" ? "All Users" : item}
            </button>
          ))}
        </div>
      </div>

      {/* DATA VIEWPORT TABLE */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-auto max-h-[70vh] md:max-h-none md:flex-1 md:min-h-0 flex flex-col">
        <table className="w-full min-w-[900px] text-sm shrink-0 [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-slate-50 [&_thead_th]:shadow-[inset_0_-1px_0_#e2e8f0]">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 border-b border-slate-200">
            <tr>
              <th className="p-3 w-12 text-center">
                <input
                  type="checkbox"
                  onChange={toggleSelectAll}
                  checked={filtered.length > 0 && selected.length === filtered.length}
                  className="cursor-pointer"
                />
              </th>
              <th className="p-3 text-left">Employee ID</th>
              <th className="p-3 text-left">User Name</th>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Contact</th>
              <th className="p-3 text-left">Department</th>
              <th className="p-3 text-left">Last Updated</th>
              <th className="p-3 w-16 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {/* SYNCED DATA LAYER ROW GENERATION */}
            {!loading && paginatedUsers.map((user) => (
              <tr
                key={user.usr_id}
                onClick={() => navigate(`/dashboard/${user.usr_id}`, { state: { userName: user.usr_name } })}
                className="border-b border-slate-100 hover:bg-slate-50 transition cursor-pointer"
              >
                <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.includes(user.usr_id)}
                    onChange={() => toggleRow(user.usr_id)}
                    className="cursor-pointer"
                  />
                </td>

                <td className="p-3">
                  <span className="font-semibold text-slate-800">
                    {user.usr_employee_id}
                  </span>
                </td>

                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                      {user.usr_name
                        ? user.usr_name.split(" ").map((x) => x[0]).join("").substring(0, 2)
                        : "U"}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-800">{user.usr_name}</div>
                      <div className="text-xs text-slate-400 font-medium">
                        {user.usr_login_flag === 1 ? (
                          <span className="text-emerald-600 font-semibold">● Online</span>
                        ) : (
                          "Employee"
                        )}
                      </div>
                    </div>
                  </div>
                </td>

                <td className="p-3 text-slate-700">{user.usr_email}</td>
                <td className="p-3 text-slate-700">{user.usr_contact}</td>

                <td className="p-3">
                  <span className="inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full border bg-blue-50 text-blue-700 border-blue-200">
                    {user.usr_department}
                  </span>
                </td>

                <td className="p-3 text-slate-600 text-xs">
                  {user.usr_last_updated ? new Date(user.usr_last_updated).toLocaleString() : "Never"}
                </td>

                {/* DROPDOWN INTERFACE MENU */}
                <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                  <div className="relative inline-block text-left" ref={activeDropdownId === user.usr_id ? dropdownRef : null}>
                    <button
                      className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-800 transition"
                      onClick={() => setActiveDropdownId(activeDropdownId === user.usr_id ? null : user.usr_id)}
                    >
                      <MoreHorizontal size={16} />
                    </button>

                    {activeDropdownId === user.usr_id && (
                      <div className="absolute right-0 mt-1.5 w-44 bg-white border border-slate-200 rounded-xl shadow-xl z-50 py-1.5 animate-in fade-in slide-in-from-top-1 duration-100">
                        {/* <button
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 text-left"
                          onClick={() => {
                            setActiveDropdownId(null);
                            console.log("Launch edit target:", user);
                          }}
                        >
                          <Pencil size={13} className="text-blue-600" />
                          <span>Edit User</span>
                        </button> */}

                        <button
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 text-left"
                          onClick={() => {
                            setActiveDropdownId(null);
                            setTargetUser({ usr_id: user.usr_id, usr_name: user.usr_name });
                          }}
                        >
                          <Target size={13} className="text-blue-600" />
                          <span>Assign Target</span>
                        </button>

                        <div className="h-[1px] bg-slate-100 my-1"></div>

                        {/* <button
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 text-left"
                          onClick={() => handleDeleteClick(user)}
                        >
                          <Trash2 size={13} />
                          <span>Delete User</span>
                        </button> */}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* LOADING STATE VIEWPORT - centred in the space under the header */}
        {loading && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-20 text-slate-400">
            <Loader2 size={32} className="animate-spin text-blue-600" />
            <span className="font-medium text-sm text-slate-500">Querying database workspace...</span>
          </div>
        )}

        {/* EMPTY STATE VIEWPORT - centred in the space under the header */}
        {!loading && filtered.length === 0 && (
          <div className="flex flex-1 items-center justify-center px-4 py-16 text-center font-medium text-slate-500">
            No Users Found
          </div>
        )}
      </div>

      {/* PAGINATION */}
      {!loading && filtered.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 px-2 shrink-0">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="font-medium">Rows per page:</span>
            <select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="cursor-pointer rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              aria-label="Rows per page"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="inline-flex items-center gap-3 bg-white border border-slate-200 rounded-full px-3 py-1.5 text-xs text-slate-600 shadow-sm">
            <span className="text-slate-400">
              {(currentPage - 1) * itemsPerPage + 1}-
              {Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length}
            </span>
            <div className="h-4 w-px bg-slate-200" />
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
              aria-label="Previous page"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="font-medium text-slate-700">
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
              aria-label="Next page"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* SLIDE OUT DRAWER INTEGRATION */}
      <AddUserDrawer
        isOpen={isAddUserOpen}
        onClose={() => {
          setIsAddUserOpen(false);
          fetchUsers(); // Instantly update row arrays upon successful user registration
        }}
      />

      <AssignTargetDrawer
        isOpen={!!targetUser}
        usrId={targetUser?.usr_id}
        usrName={targetUser?.usr_name}
        onClose={() => setTargetUser(null)}
      />
    </div>
  );
}